import type { ChatSummary } from '../api/types';

/** Смарт-папки (настройки → «Папки») — в отличие от обычных папок (см. api/folders.ts) это
 *  не ручной список chatIds, а фильтр, пересчитываемый на лету из уже загруженного списка
 *  чатов. Включатели и порядок табов — на сервере (см. api/folders.ts getFolderPrefs/
 *  updateFolderPrefs, отчёт пользователя "перенеси в базу папки") — раньше жили в localStorage
 *  и не синхронизировались между устройствами/браузерами. */

export type SmartFolderId = 'personal' | 'unread';

export const SMART_FOLDERS: {
  id: SmartFolderId;
  label: string;
  description: string;
  predicate: (c: ChatSummary) => boolean;
  /** Не скрывать таб при 0 подходящих чатов (по умолчанию пустые смарт-папки скрываются, как
   *  и обычные — см. отчёт пользователя "пустая папка не должна отображаться"). "Новые" —
   *  исключение (см. отчёт "папка новые может не пропадать если пустая"): таб то появляется,
   *  то исчезает при каждом прочтении последнего непрочитанного, что было дезориентирующим. */
  hideWhenEmpty?: boolean;
}[] = [
  {
    id: 'personal',
    label: 'smartFoldersPersonal',
    // Короткая подпись — чтобы не разбивалась на несколько строк в узкой 300px-панели настроек
    // (отчёт пользователя: длинное описание "выглядит не очень"); полная версия — в title
    // строки и кнопке «?» в углу заголовка (см. SettingsModal, раздел «Папки»).
    description: 'smartFoldersPersonalDescription',
    predicate: (c) => c.type === 1 && !c.isSupport,
    hideWhenEmpty: true,
  },
  {
    id: 'unread',
    label: 'smartFoldersUnread',
    description: 'smartFoldersUnreadDescription',
    predicate: (c) => c.unreadCount > 0,
  },
];

/** Расставляет ключи табов по переданному порядку (см. api/folders.ts FolderPrefsDto.folderOrder
 *  — раньше order читался из localStorage прямо внутри этой функции, теперь он приходит с
 *  сервера и передаётся явным параметром). Новые (ещё не встречавшиеся) ключи дописываются в
 *  конец, пропавшие (папку удалили/смарт-папку выключили) — просто выпадают. */
export function sortByFolderOrder<T extends { key: string }>(items: T[], order: string[]): T[] {
  const rank = new Map(order.map((key, i) => [key, i]));
  return [...items].sort((a, b) => {
    const ra = rank.has(a.key) ? rank.get(a.key)! : Infinity;
    const rb = rank.has(b.key) ? rank.get(b.key)! : Infinity;
    if (ra !== rb) return ra - rb;
    return 0;
  });
}
